import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import axios from "axios";
import { Plus, Trash2, Users, UserCheck, Crown, Gem, Shield, Briefcase, Edit, Key, MessageSquare, Megaphone, CreditCard, Camera, Upload, Image, Calendar, Package } from "lucide-react";
import AppShell from "@/components/layout/AppShell";
import PageHero, { HERO_IMAGES } from "@/components/common/PageHero";
import FeatureGate from "@/components/FeatureGate";
import { getRoleDisplayName, ROLE_OPTIONS } from "@/utils/roleDisplayNames";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Role access definitions
const ROLE_ACCESS_MAP = {
  'staff': {
    label: 'Staff', color: 'blue',
    access: ['Home & Dashboard', 'Sign-Ins', 'Children (View)', 'Daily Reports', 'Center Messages', 'Field Trip Tags', 'Center Menus'],
    noAccess: ['Staff Records', 'Compliance', 'Settings', 'Billing & Payments', 'User Management']
  },
  'co-owner': {
    label: 'Supervisor/Administrator', color: 'blue',
    access: ['Home & Dashboard', 'Sign-Ins', 'Children (Full)', 'Daily Reports', 'Staff Records', 'Staff Compliance', 'Child Records', 'Attendance', 'Compliance', 'Settings (Backup Only)', 'Center Messages', 'Center Menus'],
    noAccess: ['Billing & Payments', 'Full Settings', 'User Management']
  },
  'business-manager': {
    label: 'Business Manager', color: 'green',
    access: ['Home & Dashboard', 'Sign-Ins', 'Children (Full)', 'Daily Reports', 'Staff Records', 'Staff Compliance', 'Child Records', 'Attendance', 'Compliance', 'Settings (Backup Only)', 'Billing & Payments', 'Center Messages', 'Center Menus'],
    noAccess: ['Full Settings', 'User Management']
  },
  'owner': {
    label: 'Director', color: 'purple',
    access: ['All Sections — Full Access'],
    noAccess: []
  }
};

const COLOR_MAP = {
  blue: { badge: 'bg-blue-50 border-blue-200', title: 'text-blue-700', chip: 'bg-blue-100 text-blue-700', no: 'bg-gray-100 text-gray-400 line-through' },
  purple: { badge: 'bg-purple-50 border-purple-200', title: 'text-purple-700', chip: 'bg-purple-100 text-purple-700', no: 'bg-gray-100 text-gray-400 line-through' },
  amber: { badge: 'bg-amber-50 border-amber-200', title: 'text-amber-700', chip: 'bg-amber-100 text-amber-700', no: 'bg-gray-100 text-gray-400 line-through' },
  green: { badge: 'bg-green-50 border-green-200', title: 'text-green-700', chip: 'bg-green-100 text-green-700', no: 'bg-gray-100 text-gray-400 line-through' }
};

// Option A — Inline access card shown when role is selected
const RoleAccessCard = ({ role }) => {
  const info = ROLE_ACCESS_MAP[role?.toLowerCase()];
  if (!info) return null;
  const c = COLOR_MAP[info.color];
  return (
    <div className={`mt-2 rounded-lg border p-3 ${c.badge}`}>
      <p className={`text-xs font-semibold mb-2 ${c.title}`}>{info.label} — Access Summary</p>
      <div className="flex flex-wrap gap-1">
        {info.access.map(a => <span key={a} className={`text-xs px-2 py-0.5 rounded-full font-medium ${c.chip}`}>✓ {a}</span>)}
        {info.noAccess.map(a => <span key={a} className="text-xs px-2 py-0.5 rounded-full font-medium bg-gray-100 text-gray-400">✗ {a}</span>)}
      </div>
    </div>
  );
};

// Option D — Compact comparison table
const COMPARE_FEATURES = [
  { label: 'Sign-Ins & Attendance', staff: true, coowner: true, bm: true, owner: true },
  { label: 'Children Management', staff: true, coowner: true, bm: true, owner: true },
  { label: 'Daily Reports', staff: true, coowner: true, bm: true, owner: true },
  { label: 'Center Messages', staff: true, coowner: true, bm: true, owner: true },
  { label: 'Compliance Records', staff: false, coowner: true, bm: true, owner: true },
  { label: 'Staff Records', staff: false, coowner: true, bm: true, owner: true },
  { label: 'Staff Compliance', staff: false, coowner: true, bm: true, owner: true },
  { label: 'Compliance Dashboard', staff: false, coowner: true, bm: true, owner: true },
  { label: 'Settings (Backup)', staff: false, coowner: true, bm: true, owner: true },
  { label: 'Billing & Payments', staff: false, coowner: false, bm: true, owner: true },
  { label: 'Full Settings', staff: false, coowner: false, bm: false, owner: true },
  { label: 'User Management', staff: false, coowner: false, bm: false, owner: true },
];

const RoleComparisonTable = () => (
  <div className="mt-8 rounded-xl border-2 border-blue-200 overflow-hidden shadow-sm">
    <div className="bg-gray-50 px-4 py-2.5 border-b border-gray-200">
      <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Role Access Comparison</p>
    </div>
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b border-gray-100">
            <th className="text-left px-4 py-2 text-gray-500 font-medium w-48">Feature</th>
            <th className="text-center px-3 py-2 text-blue-600 font-semibold">Staff</th>
            <th className="text-center px-3 py-2 text-blue-600 font-semibold">Supervisor/<br/>Administrator</th>
            <th className="text-center px-3 py-2 text-green-600 font-semibold">Business<br/>Manager</th>
            <th className="text-center px-3 py-2 text-purple-600 font-semibold">Director</th>
          </tr>
        </thead>
        <tbody>
          {COMPARE_FEATURES.map((f, i) => (
            <tr key={f.label} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}>
              <td className="px-4 py-1.5 text-gray-600">{f.label}</td>
              {[f.staff, f.coowner, f.bm, f.owner].map((has, j) => (
                <td key={j} className="text-center px-3 py-1.5">
                  {has
                    ? <span className="text-green-500 font-bold">✓</span>
                    : <span className="text-gray-300">—</span>}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

const UserManagementPage = () => {
  const navigate = useNavigate();
  const { user: currentUser, organization } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'staff',
    isSpecialStaff: false
  });
  const [editFormData, setEditFormData] = useState({
    name: '',
    email: '',
    newPassword: '',
    role: 'staff',
    exemptFromEvaluation: false,
    permissions: {
      canAccessParentCommunications: false,
      canAccessCenterCommunications: false,
      canManagePayments: false,
      canAccessTourPlanner: false,
      canAccessInventory: false
    }
  });

  // Profile image state
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [stockAvatars, setStockAvatars] = useState([]);
  const [uploadingImage, setUploadingImage] = useState(false);
  const fileInputRef = useRef(null);

  // Check if this is the ToDays Kidz internal organization
  // Only ToDays Kidz users (owner account) should see the 2DK Staff support account
  const isTodaysKidzOrg = organization?.isOwnerAccount === true || 
                          currentUser?.email?.toLowerCase() === TODAYS_KIDZ_EMAIL.toLowerCase();

  // User Management is now available to ALL authenticated users
  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/users`);
      // Hide 2DK Staff support account from all external customers
      // Only ToDays Kidz internal users can see it
      const filteredUsers = isTodaysKidzOrg 
        ? response.data
        : response.data.filter(u => !u.isSpecialStaff);
      setUsers(filteredUsers);
    } catch (error) {
      console.error("Error fetching users:", error);
      // Staff users may not have access to full user list - that's OK
      if (error.response?.status !== 403) {
        toast.error("Failed to load users");
      }
    }
    setLoading(false);
  };

  // Fetch stock avatars
  const fetchStockAvatars = async () => {
    try {
      const response = await axios.get(`${API}/profile/stock-avatars`);
      setStockAvatars(response.data);
    } catch (error) {
      console.error("Error fetching stock avatars:", error);
    }
  };

  useEffect(() => {
    if (currentUser) {
      fetchUsers();
      fetchStockAvatars();
    }
  }, [currentUser, organization]);

  // Profile image handlers
  const handleProfileImageClick = (user) => {
    setSelectedUser(user);
    setProfileDialogOpen(true);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast.error("Please upload a JPEG, PNG, or WebP image");
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be less than 5MB");
      return;
    }

    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      await axios.post(`${API}/profile/image/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success("Profile image uploaded!");
      setProfileDialogOpen(false);
      fetchUsers();
    } catch (error) {
      console.error("Error uploading image:", error);
      toast.error(error.response?.data?.detail || "Failed to upload image");
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSelectStockAvatar = async (avatarId) => {
    setUploadingImage(true);
    try {
      await axios.post(`${API}/profile/image/select-stock?avatar_id=${avatarId}`);
      toast.success("Profile image updated!");
      setProfileDialogOpen(false);
      fetchUsers();
    } catch (error) {
      console.error("Error selecting avatar:", error);
      toast.error("Failed to update profile image");
    } finally {
      setUploadingImage(false);
    }
  };

  const handleRemoveProfileImage = async () => {
    setUploadingImage(true);
    try {
      await axios.delete(`${API}/profile/image`);
      toast.success("Profile image removed");
      setProfileDialogOpen(false);
      fetchUsers();
    } catch (error) {
      console.error("Error removing image:", error);
      toast.error("Failed to remove profile image");
    } finally {
      setUploadingImage(false);
    }
  };

  useEffect(() => {
    if (currentUser) {
      fetchUsers();
    }
  }, [currentUser, organization]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    try {
      await axios.post(`${API}/users`, formData);
      toast.success("Staff member added successfully");
      setDialogOpen(false);
      setFormData({
        name: '',
        email: '',
        password: '',
        role: 'staff',
        isSpecialStaff: false
      });
      fetchUsers();
    } catch (error) {
      console.error("Error adding user:", error);
      toast.error(error.response?.data?.detail || "Failed to add user");
    }
  };

  const handleDelete = async (userId) => {
    if (window.confirm("Are you sure you want to remove this user?")) {
      try {
        await axios.delete(`${API}/users/${userId}`);
        toast.success("User removed successfully");
        fetchUsers();
      } catch (error) {
        console.error("Error deleting user:", error);
        toast.error("Failed to remove user");
      }
    }
  };

  const handleRoleChange = async (userId, newRole, userName) => {
    try {
      await axios.put(`${API}/users/${userId}/role`, { role: newRole });
      toast.success(`${userName}'s role updated to ${getRoleDisplayName(newRole)}`);
      fetchUsers();
    } catch (error) {
      console.error("Error updating role:", error);
      toast.error(error.response?.data?.detail || "Failed to update role");
    }
  };

  // Open edit dialog with user data
  const handleEdit = (user) => {
    setEditingUser(user);
    setEditFormData({
      name: user.name || '',
      email: user.email || '',
      newPassword: '',
      role: user.role || 'staff',
      exemptFromEvaluation: user.exemptFromEvaluation || false,
      permissions: {
        canAccessParentCommunications: user.permissions?.canAccessParentCommunications || false,
        canAccessCenterCommunications: user.permissions?.canAccessCenterCommunications || false,
        canManagePayments: user.permissions?.canManagePayments || false,
        canAccessTourPlanner: user.permissions?.canAccessTourPlanner || false,
        canAccessInventory: user.permissions?.canAccessInventory || false
      }
    });
    setEditDialogOpen(true);
  };

  // Save edited user
  const handleEditSubmit = async (e) => {
    e.preventDefault();
    
    if (!editingUser) return;
    
    // Validate new password if provided
    if (editFormData.newPassword && editFormData.newPassword.length < 6) {
      toast.error("New password must be at least 6 characters");
      return;
    }

    try {
      // Sync exemptFromEvaluation to staff_records for compliance calculation (run first, independently)
      try {
        await axios.put(`${API}/staff-records/sync-exempt`, {
          email: editFormData.email,
          exemptFromEvaluation: editFormData.exemptFromEvaluation
        });
      } catch (syncErr) {
        console.warn("Could not sync exempt flag to staff records:", syncErr);
      }

      // Update user details
      await axios.put(`${API}/users/${editingUser.id}`, {
        name: editFormData.name,
        email: editFormData.email,
        password: editFormData.newPassword || undefined, // Only send if provided
        role: editFormData.role,
        exemptFromEvaluation: editFormData.exemptFromEvaluation,
        permissions: editFormData.permissions // Send permissions
      });
      
      toast.success(`${editFormData.name}'s information updated`);
      setEditDialogOpen(false);
      setEditingUser(null);
      fetchUsers();
    } catch (error) {
      console.error("Error updating user:", error);
      toast.error(error.response?.data?.detail || "Failed to update user");
    }
  };

  // Check if current user is owner or co-owner (for add/delete capabilities)
  const isOwnerOrCoOwner = currentUser?.role === 'owner' || currentUser?.role?.toLowerCase() === 'co-owner';
  
  // Check if current user is THE primary owner (first owner of the organization)
  // Use organization's ownerEmail instead of hardcoded value
  const isPrimaryOwner = currentUser?.email?.toLowerCase() === organization?.ownerEmail?.toLowerCase();

  return (
    <AppShell pageTitle="Manage Staff" breadcrumb>
      {/* Hero Banner */}
      <PageHero 
        imageUrl={HERO_IMAGES.manageStaff}
        title="Manage Staff"
        subtitle="Manage staff access and roles for your daycare"
        altText="Director planning with staff"
      />
      
      <FeatureGate feature="user_management">
      <div data-testid="user-management-page" className="relative z-10">
        {/* Action Button */}
        <div className="flex justify-end mb-6">
          {isOwnerOrCoOwner && (
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button
                  className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-semibold rounded-xl shadow-lg"
                  onClick={() => {
                    setFormData({
                      name: '',
                      email: '',
                      password: '',
                      role: 'staff',
                      isSpecialStaff: false
                    });
                  }}
                  data-testid="add-user-btn"
                >
                  <Plus className="w-5 h-5 mr-2" />
                  Add Staff Member
                </Button>
              </DialogTrigger>
                <DialogContent className="max-w-md">
                  <DialogHeader>
                    <DialogTitle className="text-2xl text-brand-primary">Add New Staff Member</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleSubmit} className="space-y-4" autoComplete="off">
                    <div>
                      <Label htmlFor="name" className="text-ink-main">Name *</Label>
                      <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="Enter staff name"
                        required
                        autoComplete="off"
                        className="border-line focus:border-brand-primary"
                        data-testid="staff-name-input"
                      />
                  </div>

                  <div>
                    <Label htmlFor="email" className="text-ink-main">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="staff@email.com"
                      required
                      autoComplete="new-email"
                      className="border-line focus:border-brand-primary"
                      data-testid="staff-email-input"
                    />
                  </div>

                <div>
                  <Label htmlFor="password" className="text-ink-main">Password *</Label>
                  <Input
                    id="password"
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder="At least 6 characters"
                    required
                    autoComplete="new-password"
                    className="border-line focus:border-brand-primary"
                    data-testid="staff-password-input"
                  />
                </div>

                <div>
                  <Label htmlFor="role" className="text-ink-main">Role *</Label>
                  <Select 
                    value={formData.role} 
                    onValueChange={(value) => setFormData({ ...formData, role: value })}
                  >
                    <SelectTrigger className="border-line focus:border-brand-primary" data-testid="role-select">
                      <SelectValue placeholder="Select role" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="staff">
                        <div className="flex items-center gap-2"><Users className="w-4 h-4 text-brand-primary" />Staff</div>
                      </SelectItem>
                      <SelectItem value="co-owner">
                        <div className="flex items-center gap-2"><Crown className="w-4 h-4 text-blue-500" />Supervisor/Administrator</div>
                      </SelectItem>
                      <SelectItem value="business-manager">
                        <div className="flex items-center gap-2"><Briefcase className="w-4 h-4 text-green-600" />Business Manager</div>
                      </SelectItem>
                      <SelectItem value="owner">
                        <div className="flex items-center gap-2"><Gem className="w-4 h-4 text-purple-600" />Director</div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  {/* Option A — Inline access card */}
                  {formData.role && <RoleAccessCard role={formData.role} />}
                </div>

                <div className="flex items-center justify-between rounded-xl border border-line px-4 py-3">
                  <div>
                    <Label className="text-ink-main">Support Account</Label>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Internal/system account — not a real staff member. Skips creating a
                      compliance record and is excluded from Staff Compliance &amp; Professional
                      Development tracking.
                    </p>
                  </div>
                  <Switch
                    checked={formData.isSpecialStaff}
                    onCheckedChange={(checked) => setFormData({ ...formData, isSpecialStaff: checked })}
                    data-testid="support-account-toggle"
                  />
                </div>

                <div className="flex gap-3">
                  <Button
                    type="submit"
                    className="flex-1 bg-brand-primary hover:bg-brand-primary/90 text-white rounded-xl"
                    data-testid="submit-staff-btn"
                  >
                    Add Staff Member
                  </Button>
                  <Button
                    type="button"
                    onClick={() => setDialogOpen(false)}
                    variant="outline"
                    className="flex-1 border-line text-ink-main rounded-xl"
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
          )}
        </div>

        {/* Users List */}
        {loading ? (
          <p className="text-center text-brand-primary">Loading...</p>
        ) : users.length === 0 ? (
          <Card className="bg-white border border-line shadow-card">
            <CardContent className="p-12 text-center">
              <Users className="w-16 h-16 text-brand-primary/40 mx-auto mb-4" />
              <p className="text-xl text-ink-main mb-4">No staff members added yet</p>
              {isOwnerOrCoOwner && (
                <Button
                  onClick={() => setDialogOpen(true)}
                  className="bg-brand-primary hover:bg-brand-primary/90 text-white rounded-xl"
                >
                  <Plus className="w-5 h-5 mr-2" />
                  Add Your First Staff Member
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {users.map((user) => (
              <Card key={user.id} className={`bg-white border-2 border-blue-200 shadow-[0_4px_14px_rgba(0,0,0,0.08)] hover:shadow-[0_12px_28px_rgba(0,0,0,0.15)] hover:-translate-y-1 transition-all duration-300`}>
                <CardHeader>
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-4 min-w-0">
                      {/* Profile Image/Avatar */}
                      <div 
                        className="relative cursor-pointer group"
                        onClick={() => handleProfileImageClick(user)}
                      >
                        {user.profileImage ? (
                          <div className="w-14 h-14 rounded-xl overflow-hidden border-2 border-white shadow-md">
                            <img 
                              src={user.profileImage} 
                              alt={user.name}
                              className="w-full h-full object-cover"
                            />
                          </div>
                        ) : (
                          <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${user.isSpecialStaff ? 'bg-brand-secondary/10' : user.role === 'owner' ? 'bg-purple-100' : user.role?.toLowerCase() === 'co-owner' ? 'bg-blue-100' : user.role?.toLowerCase() === 'business-manager' ? 'bg-green-100' : 'bg-brand-primary/10'}`}>
                            {user.role === 'owner' ? (
                              <Gem className="w-7 h-7 text-purple-600" />
                            ) : user.role?.toLowerCase() === 'co-owner' ? (
                              <Key className="w-7 h-7 text-blue-500" />
                            ) : user.role?.toLowerCase() === 'business-manager' ? (
                              <Briefcase className="w-7 h-7 text-green-600" />
                            ) : user.isSpecialStaff ? (
                              <Shield className="w-7 h-7 text-brand-secondary" />
                            ) : (
                              <Users className="w-7 h-7 text-brand-primary" />
                            )}
                          </div>
                        )}
                        {/* Camera overlay for all users — owner can update any */}
                        {isOwnerOrCoOwner && (
                          <div className="absolute inset-0 bg-black/50 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                            <Camera className="w-5 h-5 text-white" />
                          </div>
                        )}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <CardTitle className="text-xl text-ink-main">
                            {user.isSpecialStaff ? "2DK Staff" : user.name}
                          </CardTitle>
                          {user.isSpecialStaff && (
                            <span className="px-2 py-0.5 bg-brand-secondary text-white text-xs font-semibold rounded-full">
                              Support Account
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-ink-muted mt-1">{user.email}</p>
                      </div>
                    </div>
                    {/* Option A — Access card in the center empty space */}
                    {user.role && (() => {
                      const info = ROLE_ACCESS_MAP[user.role?.toLowerCase()];
                      if (!info) return null;
                      const c = COLOR_MAP[info.color];
                      return (
                        <div className={`flex-1 mx-4 rounded-lg border px-3 py-2 ${c.badge}`}>
                          <p className={`text-xs font-semibold mb-1.5 ${c.title}`}>
                            {info.label} — Can Access
                          </p>
                          <div className="flex flex-wrap gap-1">
                            {info.access.map(a => (
                              <span key={a} className={`text-xs px-2 py-0.5 rounded-full font-medium ${c.chip}`}>✓ {a}</span>
                            ))}
                            {info.noAccess.map(a => (
                              <span key={a} className="text-xs px-2 py-0.5 rounded-full font-medium bg-gray-100 text-gray-400">✗ {a}</span>
                            ))}
                          </div>
                        </div>
                      );
                    })()}
                    <div className="flex items-center gap-3">
                      {/* Role selector - show for all users except primary owner and current user */}
                      {isOwnerOrCoOwner && user.email?.toLowerCase() !== organization?.ownerEmail?.toLowerCase() && user.id !== currentUser?.id ? (
                        <Select 
                          value={user.role} 
                          onValueChange={(newRole) => handleRoleChange(user.id, newRole, user.name)}
                        >
                          <SelectTrigger className={`w-44 ${user.isSpecialStaff ? 'border-brand-secondary' : 'border-line'}`} data-testid={`role-select-${user.id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="staff">Staff</SelectItem>
                            <SelectItem value="co-owner">Supervisor/Administrator</SelectItem>
                            <SelectItem value="business-manager">Business Manager</SelectItem>
                            <SelectItem value="owner">Director</SelectItem>
                          </SelectContent>
                        </Select>
                      ) : (
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          user.isSpecialStaff
                            ? 'bg-brand-secondary/10 text-brand-secondary'
                            : user.role === 'owner'
                            ? 'bg-purple-100 text-purple-600'
                            : user.role?.toLowerCase() === 'co-owner'
                            ? 'bg-blue-100 text-blue-600'
                            : user.role?.toLowerCase() === 'business-manager'
                            ? 'bg-green-100 text-green-700'
                            : 'bg-brand-primary/10 text-brand-primary'
                        }`}>
                          {getRoleDisplayName(user.role)}
                        </span>
                      )}
                      
                      {/* Permission Icons - Show for staff with special permissions */}
                      {user.role === 'staff' && (user.permissions?.canAccessParentCommunications || user.permissions?.canAccessCenterCommunications || user.permissions?.canManagePayments || user.permissions?.canAccessTourPlanner || user.permissions?.canAccessInventory) && (
                        <div className="flex items-center gap-1 ml-2">
                          {user.permissions?.canAccessParentCommunications && (
                            <div className="p-1 rounded bg-blue-100 text-blue-600" title="Parent Communications Access">
                              <MessageSquare className="w-3.5 h-3.5" />
                            </div>
                          )}
                          {user.permissions?.canAccessCenterCommunications && (
                            <div className="p-1 rounded bg-purple-100 text-purple-600" title="Center Communications Access">
                              <Megaphone className="w-3.5 h-3.5" />
                            </div>
                          )}
                          {user.permissions?.canManagePayments && (
                            <div className="p-1 rounded bg-green-100 text-green-600" title="Payments & Invoicing Access">
                              <CreditCard className="w-3.5 h-3.5" />
                            </div>
                          )}
                          {user.permissions?.canAccessTourPlanner && (
                            <div className="p-1 rounded bg-teal-100 text-teal-600" title="Tour & Ratio Planner Access">
                              <Calendar className="w-3.5 h-3.5" />
                            </div>
                          )}
                          {user.permissions?.canAccessInventory && (
                            <div className="p-1 rounded bg-amber-100 text-amber-600" title="Menu, Inventory & Shopping List Access">
                              <Package className="w-3.5 h-3.5" />
                            </div>
                          )}
                        </div>
                      )}
                      {/* Edit button - shown for owners to edit other users (not primary owner) */}
                      {isOwnerOrCoOwner && user.email?.toLowerCase() !== organization?.ownerEmail?.toLowerCase() && !user.isSpecialStaff && (
                        <Button
                          onClick={() => handleEdit(user)}
                          variant="outline"
                          size="sm"
                          className="border-brand-primary/30 bg-brand-primary/5 text-brand-primary hover:bg-brand-primary/10"
                          data-testid={`edit-user-${user.id}`}
                        >
                          <Edit className="w-4 h-4 mr-1" />
                          Edit
                        </Button>
                      )}
                      {/* Delete button - not shown for primary owner, current user, or special staff */}
                      {isOwnerOrCoOwner && user.email?.toLowerCase() !== organization?.ownerEmail?.toLowerCase() && user.id !== currentUser?.id && !user.isSpecialStaff && (
                        <Button
                          onClick={() => handleDelete(user.id)}
                          variant="outline"
                          size="sm"
                          className="border-danger/30 text-danger hover:bg-danger/5"
                          data-testid={`delete-user-${user.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </CardHeader>
              </Card>
            ))}
          </div>
        )}

        {/* Edit User Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="text-2xl text-brand-primary flex items-center gap-2">
                <Edit className="w-6 h-6" />
                Edit User
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleEditSubmit} className="space-y-4">
              <div>
                <Label htmlFor="edit-name" className="text-ink-main">Name *</Label>
                <Input
                  id="edit-name"
                  value={editFormData.name}
                  onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                  placeholder="Enter name"
                  required
                  className="border-line focus:border-brand-primary"
                  data-testid="edit-name-input"
                />
              </div>

              <div>
                <Label htmlFor="edit-email" className="text-ink-main">Email *</Label>
                <Input
                  id="edit-email"
                  type="email"
                  value={editFormData.email}
                  onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
                  placeholder="email@example.com"
                  required
                  className="border-line focus:border-brand-primary"
                  data-testid="edit-email-input"
                />
              </div>

              <div>
                <Label htmlFor="edit-password" className="text-ink-main flex items-center gap-2">
                  <Key className="w-4 h-4" />
                  New Password <span className="text-xs text-ink-muted">(leave blank to keep current)</span>
                </Label>
                <Input
                  id="edit-password"
                  type="password"
                  value={editFormData.newPassword}
                  onChange={(e) => setEditFormData({ ...editFormData, newPassword: e.target.value })}
                  placeholder="Enter new password (optional)"
                  className="border-line focus:border-brand-primary"
                  data-testid="edit-password-input"
                />
                <p className="text-xs text-ink-muted mt-1">Minimum 6 characters if changing</p>
              </div>

              <div>
                <Label htmlFor="edit-role" className="text-ink-main">Role *</Label>
                <Select 
                  value={editFormData.role} 
                  onValueChange={(value) => setEditFormData({ ...editFormData, role: value })}
                >
                  <SelectTrigger className="border-line focus:border-brand-primary" data-testid="edit-role-select">
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="staff">
                      <div className="flex items-center gap-2">
                        <Users className="w-4 h-4 text-brand-primary" />
                        Staff
                      </div>
                    </SelectItem>
                    <SelectItem value="co-owner">
                      <div className="flex items-center gap-2">
                        <Crown className="w-4 h-4 text-green-600" />
                        Supervisor/Administrator
                      </div>
                    </SelectItem>
                    <SelectItem value="owner">
                      <div className="flex items-center gap-2">
                        <Gem className="w-4 h-4 text-purple-600" />
                        Director
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Staff Permissions - Only show for staff role */}
              {editFormData.role === 'staff' && (
                <div className="space-y-3 pt-2 border-t border-line">
                  <Label className="text-ink-main font-semibold">Staff Permissions</Label>
                  <p className="text-xs text-ink-muted">Grant additional access to this staff member</p>
                  
                  <div className="space-y-3">
                    <div className="flex items-center space-x-3">
                      <Checkbox
                        id="perm-parent-comm"
                        checked={editFormData.permissions.canAccessParentCommunications}
                        onCheckedChange={(checked) => setEditFormData({
                          ...editFormData,
                          permissions: { ...editFormData.permissions, canAccessParentCommunications: checked }
                        })}
                      />
                      <Label htmlFor="perm-parent-comm" className="flex items-center gap-2 text-sm cursor-pointer">
                        <MessageSquare className="w-4 h-4 text-brand-secondary" />
                        Allow Parent Communications Access
                      </Label>
                    </div>

                    <div className="flex items-center space-x-3">
                      <Checkbox
                        id="perm-center-comm"
                        checked={editFormData.permissions.canAccessCenterCommunications}
                        onCheckedChange={(checked) => setEditFormData({
                          ...editFormData,
                          permissions: { ...editFormData.permissions, canAccessCenterCommunications: checked }
                        })}
                      />
                      <Label htmlFor="perm-center-comm" className="flex items-center gap-2 text-sm cursor-pointer">
                        <Megaphone className="w-4 h-4 text-brand-primary" />
                        Allow Center Communications Access
                      </Label>
                    </div>

                    <div className="flex items-center space-x-3">
                      <Checkbox
                        id="perm-payments"
                        checked={editFormData.permissions.canManagePayments}
                        onCheckedChange={(checked) => setEditFormData({
                          ...editFormData,
                          permissions: { ...editFormData.permissions, canManagePayments: checked }
                        })}
                      />
                      <Label htmlFor="perm-payments" className="flex items-center gap-2 text-sm cursor-pointer">
                        <CreditCard className="w-4 h-4 text-purple-600" />
                        Allow Payments & Invoicing Access
                      </Label>
                    </div>

                    <div className="flex items-center space-x-3">
                      <Checkbox
                        id="perm-tour-planner"
                        data-testid="perm-tour-planner"
                        checked={editFormData.permissions.canAccessTourPlanner}
                        onCheckedChange={(checked) => setEditFormData({
                          ...editFormData,
                          permissions: { ...editFormData.permissions, canAccessTourPlanner: checked }
                        })}
                      />
                      <Label htmlFor="perm-tour-planner" className="flex items-center gap-2 text-sm cursor-pointer">
                        <Calendar className="w-4 h-4 text-teal-600" />
                        Allow Tour &amp; Ratio Planner Access
                      </Label>
                    </div>

                    <div className="flex items-center space-x-3">
                      <Checkbox
                        id="perm-inventory"
                        data-testid="perm-inventory"
                        checked={editFormData.permissions.canAccessInventory}
                        onCheckedChange={(checked) => setEditFormData({
                          ...editFormData,
                          permissions: { ...editFormData.permissions, canAccessInventory: checked }
                        })}
                      />
                      <Label htmlFor="perm-inventory" className="flex items-center gap-2 text-sm cursor-pointer">
                        <Package className="w-4 h-4 text-amber-600" />
                        Allow Menu, Inventory &amp; Shopping List Access
                      </Label>
                    </div>
                  </div>
                </div>
              )}

              {/* Evaluation Exempt Toggle */}
              <div className="flex items-center space-x-3 pt-2 border-t border-line">
                <Switch
                  id="exempt-eval"
                  checked={editFormData.exemptFromEvaluation}
                  onCheckedChange={(checked) => setEditFormData({ ...editFormData, exemptFromEvaluation: checked })}
                  data-testid="exempt-from-evaluation-toggle"
                />
                <Label htmlFor="exempt-eval" className="text-sm cursor-pointer">
                  <span className="font-semibold text-ink-main">Exempt from Annual Evaluation</span>
                  <span className="block text-xs text-ink-muted">Will not be counted in evaluation compliance (e.g., Director/Owner)</span>
                </Label>
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  type="submit"
                  className="flex-1 bg-brand-primary hover:bg-brand-primary/90 text-white rounded-xl"
                  data-testid="save-edit-btn"
                >
                  Save Changes
                </Button>
                <Button
                  type="button"
                  onClick={() => { setEditDialogOpen(false); setEditingUser(null); }}
                  variant="outline"
                  className="flex-1 border-line text-ink-main rounded-xl"
                >
                  Cancel
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Profile Image Dialog */}
        <Dialog open={profileDialogOpen} onOpenChange={setProfileDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-xl text-brand-primary">Update Profile Picture</DialogTitle>
            </DialogHeader>
            
            <Tabs defaultValue="upload" className="mt-4">
              <TabsList className="grid grid-cols-2 bg-gray-100 p-1 rounded-lg">
                <TabsTrigger value="upload" className="data-[state=active]:bg-white rounded-md">
                  <Upload className="w-4 h-4 mr-2" />
                  Upload Photo
                </TabsTrigger>
                <TabsTrigger value="stock" className="data-[state=active]:bg-white rounded-md">
                  <Image className="w-4 h-4 mr-2" />
                  Stock Images
                </TabsTrigger>
              </TabsList>

              <TabsContent value="upload" className="mt-4">
                <div className="text-center py-8">
                  {/* Current Image Preview */}
                  {selectedUser?.profileImage && (
                    <div className="mb-4">
                      <img 
                        src={selectedUser.profileImage} 
                        alt="Current profile"
                        className="w-24 h-24 rounded-xl mx-auto object-cover border-2 border-line"
                      />
                      <p className="text-sm text-ink-muted mt-2">Current photo</p>
                    </div>
                  )}
                  
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileUpload}
                    accept="image/jpeg,image/png,image/jpg,image/webp"
                    className="hidden"
                  />
                  
                  <Button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingImage}
                    className="bg-brand-primary hover:bg-brand-primary/90 text-white"
                  >
                    <Camera className="w-4 h-4 mr-2" />
                    {uploadingImage ? "Uploading..." : "Choose Photo"}
                  </Button>
                  <p className="text-xs text-ink-muted mt-3">
                    JPEG, PNG, or WebP. Max 5MB.
                  </p>
                </div>
              </TabsContent>

              <TabsContent value="stock" className="mt-4">
                <div className="grid grid-cols-5 gap-3">
                  {stockAvatars.map((avatar) => (
                    <button
                      key={avatar.id}
                      onClick={() => handleSelectStockAvatar(avatar.id)}
                      disabled={uploadingImage}
                      className={`relative rounded-xl overflow-hidden border-2 transition-all hover:scale-105 ${
                        selectedUser?.profileImage === avatar.url 
                          ? 'border-brand-primary ring-2 ring-brand-primary/30' 
                          : 'border-transparent hover:border-brand-primary/50'
                      }`}
                    >
                      <img 
                        src={avatar.url} 
                        alt={avatar.label}
                        className="w-full aspect-square object-cover"
                      />
                    </button>
                  ))}
                </div>
              </TabsContent>
            </Tabs>

            <DialogFooter className="mt-4">
              {selectedUser?.profileImage && (
                <Button
                  variant="outline"
                  onClick={handleRemoveProfileImage}
                  disabled={uploadingImage}
                  className="text-red-500 border-red-200 hover:bg-red-50"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Remove Photo
                </Button>
              )}
              <Button
                variant="outline"
                onClick={() => setProfileDialogOpen(false)}
              >
                Cancel
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      <RoleComparisonTable />
      </FeatureGate>
    </AppShell>
  );
};

export default UserManagementPage;
